class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.tabs = this.querySelectorAll('.js-predictive-tab');
    this.header = document.querySelector('.header');
    if (this.querySelector("[data-predictive-search]")) {
      this.predictiveSearchResults = this.querySelector(
        "[data-predictive-search]"
      ).querySelector(".predictive-search__results");
    }
    this.isOpen = false;
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.closeIcons = this.querySelectorAll('.header__close .icon');
    this.recentSearches = this.querySelector('.js-recent-searches');

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
    this.closeIcons.forEach((icon) => {
      icon.addEventListener("click", () => { this.close(true) });
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    } else {
      const query = this.getQuery();

      try {
        let data = localStorage.getItem("searches");
        let searchData = data ? JSON.parse(data) : [];

        searchData.push(query);
        localStorage.setItem('searches', JSON.stringify(searchData))
      } catch (error) {
        console.error(error);
      }
    }
  }

  onFocus() {
    this.classList.add('header__search--focussed');
    this.open();
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    event.preventDefault();

    if (this.getQuery().length > 0) {
      this.querySelector('.search .header__close').style.display = "block";
      this.querySelector('.search .icon--search').classList.add('hidden');
      this.classList.add('header__search--focussed');
    }

    if(this.getQuery().length === 0) {
      this.header.classList.remove('search-open');
      this.classList.add('header__search--hidden');
      this.classList.remove('header__search--focussed');
    }

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector("li");

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", "true");

    if (selectedElement) selectedElement.setAttribute("aria-selected", "false");

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button'
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[options][unavailable_products]=hide&resources[options][fields]=title,product_type,variants.title&resources[limit_scope]=each&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);

          this.close();

          throw error;
        }

        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();

        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", "true");
    this.setLiveRegionResults();

    this.tabs = this.predictiveSearchResults.querySelectorAll('.js-predictive-tab');
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", () => this.toggleTabs(tab));
    });

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  open() {
    this.populateRecentSearches();
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.applyShadow(true);

    // Recalculate Header Height
    const calculateHeaderHeight = new Event("calculate-header-height");
    document.dispatchEvent(calculateHeaderHeight);
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) {
        this.input.value = "";
      }

      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    if (this.input) {
      this.input.setAttribute("aria-activedescendant", "");
      this.input.setAttribute("aria-expanded", false);
    }

    this.removeAttribute("open");
    this.resultsMaxHeight = false;

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute("style");
    }

    this.applyShadow(false);

    this.querySelector('.search .header__close').style.display = "none";
    this.querySelector('.search .icon--search').classList.remove('hidden');
    this.classList.remove('header__search--focussed');
    this.classList.add('header__search--hidden');
    this.header.classList.remove('search-open');
    this.isOpen = false;

    document.body.style.position = '';

    // Recalculate Header Height
    const calculateHeaderHeight = new Event("calculate-header-height");
    document.dispatchEvent(calculateHeaderHeight);
  }

  //Body click event to close drawer
  handleBodyClick(evt) {
    const target = evt.target;

    if (!target.closest("predictive-search") && !target.closest("search-trigger") && !target.querySelector("predictive-search")) {
      this.close(true);
      this.classList.remove('header__search--focussed');
    }
  }

  toggleTabs(tab) {
    this.tabs.forEach((tab) => {
      tab.classList.remove('active');
    });

    tab.classList.add('active');

    const tabId = tab.dataset.tab;
    const tabContent = this.querySelectorAll('.js-results-tabs > div');

    tabContent.forEach((content) => {
      content.classList.add('hidden');

      if (content.id == tabId) {
        content.classList.remove('hidden');
      }
    });
  }

  applyShadow(shadow) {
    if (shadow) {
      document.body.classList.add("shadow--body");

    } else {
      document.body.classList.remove("shadow--body");
    }

    document.body.addEventListener("click", this.onBodyClick, {
      passive: true,
    });
  }

  populateRecentSearches() {
    if (!localStorage.getItem("searches")) return;
    let searches = JSON.parse(localStorage.getItem("searches"));

    if (searches.length > 4) searches.shift();
    searches = searches.slice(-4);

    if (searches) {
      const searchesContainer = this.recentSearches.querySelector('.predictive-search__results-list');
      searchesContainer.innerHTML = '';

      let searchList = '';
      searches.forEach((query) => {
        let listItem = `<li
              class="predictive-search__list-item"
              role="option"
              aria-selected="false"
            >
              <a
                href="${routes.search}?q=${query}"
                class="predictive-search__item"
                tabindex="-1"
              >
                <div class="predictive-search__item-content">
                  <span class="predictive-search__item-heading">${query}</span>
                </div>
              </a>
            </li>`

        searchList = searchList.concat(listItem);
        searchesContainer.innerHTML = searchList;
      });

      this.recentSearches.classList.remove('hidden');
    } else {
      this.recentSearches.classList.add('hidden');
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
